import type { ReactNode } from 'react';

import { Trans } from '@lingui/react/macro';
import { Share04 } from '@untitledui/icons/Share04';

import { ExternalLinkButton } from '@/components/base/buttons/button';
import { cx } from '@/utils/cx';

/**
 * A titled card on an account settings page. Each page is one or more of
 * these, so every section reads the same way.
 */
export function SettingsSection({
  title,
  description,
  helpUrl,
  children,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  /** Help center article for this page, opened in a new tab. */
  helpUrl?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cx(
        'flex flex-col gap-6 rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary',
        className,
      )}
    >
      <header className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-primary">{title}</h2>
          {description && (
            <p className="text-sm text-tertiary">{description}</p>
          )}
        </div>
        {helpUrl && (
          <ExternalLinkButton
            href={helpUrl}
            target="_blank"
            color="link-color"
            size="sm"
            iconTrailing={Share04}
            className="shrink-0"
          >
            <Trans>Help</Trans>
          </ExternalLinkButton>
        )}
      </header>
      {children}
    </section>
  );
}

/** Placeholder with the same footprint as a {@link SettingsSection}. */
export function SettingsSectionSkeleton() {
  return (
    <div className="h-80 animate-pulse rounded-xl bg-primary shadow-xs ring-1 ring-secondary" />
  );
}
